import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Chip } from 'primereact/chip';
import { Tooltip } from 'primereact/tooltip';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import 'primereact/resources/themes/lara-light-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import * as echarts from 'echarts';
import apiClient from '../../../stateManagers/services/axiosInterceptor';
import { useSchoolDetailsStore, SessionDetails } from '../../../stateManagers/hooks/useSchoolDetailsStore';

// ==================== TYPES ====================
// Matches historyRecordResponse from GET /api/fee-records/history — this is
// the data source for the "last paid records" table.
interface HistoryRecord {
  student_id: string;
  student_name: string;
  admission_no: string;
  profile_picture_url: string;
  class_name: string;
  section: string | null;
  roll_no: number;
  fee_record_id: string;
  month_year: string;
  receipt_no: string | null;
  paid_date: string | null;
  total_amount: number | null;
  paid_amount: number | null;
  fees_status: string;
  pay_mode: string | null;
  is_exempted: boolean;
}

interface Counts {
  total_students: number;
  paid_students: number;
  unpaid_students: number;
}

// ==================== SESSION MONTH HELPERS ====================
// The pie chart's dashboard-summary endpoint still requires a month_year —
// these helpers resolve that internally (current month, falling back to the
// latest started session month) without exposing any picker in the UI.
const MONTH_FULL = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

interface MonthOption {
  value: string; // 'YYYY-MM'
}

const generateSessionMonths = (session: SessionDetails | null | undefined): MonthOption[] => {
  if (!session || !session.start_month || !session.end_month) return [];

  const { start_month, end_month } = session;
  if (start_month < 1 || start_month > 12 || end_month < 1 || end_month > 12) return [];

  const yearMatch = session.session?.match(/^(\d{4})/);
  const startYear = yearMatch
    ? parseInt(yearMatch[1], 10)
    : session.start_date
    ? new Date(session.start_date).getFullYear()
    : new Date().getFullYear();

  const wraps = end_month < start_month;
  const totalMonths = wraps ? (12 - start_month + 1) + end_month : (end_month - start_month + 1);

  const months: MonthOption[] = [];
  let year = startYear;
  let month = start_month;

  for (let i = 0; i < totalMonths; i++) {
    const mm = String(month).padStart(2, '0');
    months.push({ value: `${year}-${mm}` });

    month += 1;
    if (month > 12) {
      month = 1;
      year += 1;
    }
  }

  return months;
};

const getDefaultMonthYear = (months: MonthOption[]): string => {
  if (months.length === 0) return '';

  const now = new Date();
  const currentMonthYear = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

  const exact = months.find((m) => m.value === currentMonthYear);
  if (exact) return exact.value;

  const pastOrCurrent = months.filter((m) => m.value <= currentMonthYear);
  if (pastOrCurrent.length > 0) return pastOrCurrent[pastOrCurrent.length - 1].value;

  return months[0].value;
};

const formatMonthYearLabel = (monthYear: string): string => {
  if (!monthYear) return '';
  const [year, month] = monthYear.split('-');
  const idx = parseInt(month, 10) - 1;
  return `${MONTH_FULL[idx] ?? month} ${year}`;
};

/** Start/end date (YYYY-MM-DD) for the current calendar month — used by the
 * "This Month" segment to scope the history fetch via start_date/end_date. */
const getCurrentMonthDateRange = (): { start_date: string; end_date: string } => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth(); // 0-indexed
  const pad = (n: number) => String(n).padStart(2, '0');

  const start_date = `${year}-${pad(month + 1)}-01`;
  const lastDay = new Date(year, month + 1, 0).getDate();
  const end_date = `${year}-${pad(month + 1)}-${pad(lastDay)}`;

  return { start_date, end_date };
};

// ==================== HOOK: last N paid records (history API) ====================
// dateRange is optional — omit it for a plain "All" fetch (no start_date/
// end_date, same as before), or pass {start_date, end_date} to scope it
// (used by the "This Month" segment). No date inputs are ever shown in the
// UI — the range is always computed, never typed.
const useFeeHistory = (
  sessionId: string | undefined,
  limit: number = 5,
  dateRange: { start_date: string; end_date: string } | null = null,
) => {
  const [records, setRecords] = useState<HistoryRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!sessionId) {
      setError('No current session found. Please select a session first.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const params: Record<string, string | number> = {
        session_id: sessionId,
        limit,
        page: 1,
      };
      if (dateRange) {
        params.start_date = dateRange.start_date;
        params.end_date = dateRange.end_date;
      }

      const response = await apiClient.get('/api/fee-records/history', { params });

      if (response.data.status !== 'success') {
        throw new Error(response.data.message || 'Failed to fetch payment history');
      }

      setRecords(response.data.data || []);
    } catch (err: any) {
      console.error('Failed to fetch fee history:', err);
      setError(err.message || 'Failed to load payment history. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [sessionId, limit, dateRange?.start_date, dateRange?.end_date]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { records, loading, error, refetch: fetchData };
};

// ==================== HOOK: pie chart counts (dashboard-summary API, unchanged) ====================
const useFeeCounts = (sessionId: string | undefined, monthYear: string) => {
  const [counts, setCounts] = useState<Counts | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!sessionId) {
      setError('No current session found. Please select a session first.');
      setLoading(false);
      return;
    }
    if (!monthYear) {
      setLoading(true);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.get('/api/fee-records/dashboard-summary', {
        params: {
          session_id: sessionId,
          month_year: monthYear,
        },
      });

      if (response.data.status !== 'success') {
        throw new Error(response.data.message || 'Failed to fetch fee summary');
      }

      setCounts(response.data.counts);
    } catch (err: any) {
      console.error('Failed to fetch fee counts:', err);
      setError(err.message || 'Failed to load fee summary. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [sessionId, monthYear]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { counts, loading, error, refetch: fetchData };
};

// ==================== ECHARTS PIE CHART ====================
const EChartsPieChart: React.FC<{ data: Counts }> = ({ data }) => {
  const chartRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!chartRef.current) return;

    const chart = echarts.init(chartRef.current);
    const option = {
      tooltip: {
        trigger: 'item',
        formatter: '{b}: {c} ({d}%)',
        backgroundColor: '#1e40af',
        borderColor: '#1e40af',
        borderWidth: 0,
        borderRadius: 8,
        textStyle: {
          color: 'white',
          fontSize: 11,
          fontWeight: 600,
        },
        extraCssText: 'box-shadow: 0 4px 12px rgba(0,0,0,0.2);',
        padding: [6, 10],
      },
      legend: {
        orient: 'horizontal',
        bottom: 8,
        left: 'center',
        textStyle: {
          color: '#64748b',
          fontSize: 11,
          fontWeight: 600,
        },
        formatter: (name: string) => {
          let value;
          switch (name) {
            case 'Paid':
              value = data.paid_students;
              break;
            case 'Unpaid':
              value = data.unpaid_students;
              break;
            case 'Total':
              value = data.total_students;
              break;
            default:
              value = 0;
          }
          return `${name}: ${value}`;
        },
        itemGap: 16,
        itemWidth: 8,
        itemHeight: 8,
        icon: 'circle',
      },
      series: [
        {
          type: 'pie',
          radius: ['50%', '80%'],
          center: ['50%', '45%'],
          avoidLabelOverlap: true,
          itemStyle: {
            borderRadius: 10,
            borderColor: '#fff',
            borderWidth: 2,
            shadowBlur: 12,
            shadowColor: 'rgba(0, 0, 0, 0.2)',
          },
          label: {
            show: true,
            position: 'inside',
            formatter: '{c}',
            fontSize: 13,
            fontWeight: 700,
            color: '#fff',
            textShadowColor: 'rgba(0, 0, 0, 0.4)',
            textShadowBlur: 2,
            textShadowOffsetX: 1,
            textShadowOffsetY: 1,
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 20,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.3)',
            },
            label: {
              fontSize: 14,
              fontWeight: 800,
            },
          },
          data: [
            {
              value: data.paid_students,
              name: 'Paid',
              itemStyle: {
                color: {
                  type: 'linear',
                  x: 0,
                  y: 0,
                  x2: 0,
                  y2: 1,
                  colorStops: [
                    { offset: 0, color: '#22c55e' },
                    { offset: 1, color: '#16a34a' },
                  ],
                },
              },
            },
            {
              value: data.unpaid_students,
              name: 'Unpaid',
              itemStyle: {
                color: {
                  type: 'linear',
                  x: 0,
                  y: 0,
                  x2: 0,
                  y2: 1,
                  colorStops: [
                    { offset: 0, color: '#ef4444' },
                    { offset: 1, color: '#dc2626' },
                  ],
                },
              },
            },
            {
              value: data.total_students,
              name: 'Total',
              itemStyle: {
                color: {
                  type: 'linear',
                  x: 0,
                  y: 0,
                  x2: 0,
                  y2: 1,
                  colorStops: [
                    { offset: 0, color: '#3b82f6' },
                    { offset: 1, color: '#1d4ed8' },
                  ],
                },
              },
            },
          ],
        },
      ],
    };

    chart.setOption(option);

    // ResizeObserver catches any change to the container's real pixel size —
    // window resizes, and CSS/flex-driven changes (like the height stretch
    // from the row's default align-items: stretch) that a plain window
    // 'resize' listener would never see. The resize() call is deferred to
    // the next animation frame rather than run synchronously inside the
    // observer callback — calling it synchronously can trigger a layout
    // recalculation within the same observation cycle, which is exactly
    // what causes the browser's (harmless but noisy) "ResizeObserver loop
    // completed with undelivered notifications" warning.
    let rafId: number | null = null;
    const resizeObserver = new ResizeObserver(() => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => chart.resize());
    });
    resizeObserver.observe(chartRef.current);

    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      resizeObserver.disconnect();
      chart.dispose();
    };
  }, [data]);

  return <div ref={chartRef} style={{ width: '100%', height: '100%', minHeight: '280px' }} />;
};

// ==================== SEGMENTED CONTROL (All / This Month) ====================
// No date inputs are ever rendered — this only toggles between the plain
// unscoped fetch and a fetch scoped to the current calendar month, both
// computed internally. Styled to match TodayCollectionCard's PeriodSwitcher:
// light gray track, active pill on white with a soft blue shadow, inactive
// labels in muted gray — same visual language as the rest of the fees UI.
interface SegmentedControlProps {
  value: 'all' | 'month';
  onChange: (value: 'all' | 'month') => void;
  disabled?: boolean;
}

const SegmentedControl: React.FC<SegmentedControlProps> = ({ value, onChange, disabled }) => {
  const options: { key: 'all' | 'month'; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'month', label: 'This Month' },
  ];

  return (
    <div
      style={{
        display: 'flex',
        gap: 2,
        background: '#f1f5f9',
        borderRadius: 999,
        padding: 3,
        border: '1px solid #e2e8f0',
        opacity: disabled ? 0.6 : 1,
      }}
    >
      {options.map((opt) => {
        const isActive = value === opt.key;
        return (
          <button
            key={opt.key}
            type="button"
            disabled={disabled}
            onClick={() => !disabled && onChange(opt.key)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: opt.key === 'all' ? '5px 10px' : '5px 10px',
              borderRadius: 999,
              border: 'none',
              cursor: disabled ? 'not-allowed' : 'pointer',
              background: isActive ? '#ffffff' : 'transparent',
              boxShadow: isActive ? '0 1px 4px rgba(30, 64, 175, 0.15)' : 'none',
              transition: 'background 0.2s ease, box-shadow 0.2s ease',
            }}
          >
            {opt.key === 'all' && (
              <i
                className="pi pi-th-large"
                style={{ fontSize: 10, color: isActive ? '#1e40af' : '#94a3b8' }}
              ></i>
            )}
            <span
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: isActive ? '#1e40af' : '#94a3b8',
              }}
            >
              {opt.label}
            </span>
          </button>
        );
      })}
    </div>
  );
};

// Reserved height (px) for the last-paid-records table region — sized to
// comfortably fit the 5-row fetch limit plus its header, so switching
// between the "All" and "This Month" segments (which can return anywhere
// from 0 to 5 rows) never changes how much vertical space the table takes.
const HISTORY_TABLE_MIN_HEIGHT = 258;

// ==================== MAIN COMPONENT ====================
const FeesSummary: React.FC = () => {
  const { schoolDetails } = useSchoolDetailsStore();
  const sessionId = schoolDetails?.current_session?.id;

  // Resolved automatically for the pie chart only — no picker, no manual control.
  const months = useMemo(
    () => generateSessionMonths(schoolDetails?.current_session),
    [schoolDetails?.current_session]
  );
  const currentMonthYear = useMemo(() => getDefaultMonthYear(months), [months]);

  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);

  // Segmented control state — 'all' fetches the plain unscoped last-N feed
  // (no date params), 'month' scopes it to the current calendar month via
  // start_date/end_date. No date inputs are shown; the range is computed.
  const [historyRange, setHistoryRange] = useState<'all' | 'month'>('all');
  const currentMonthDateRange = useMemo(() => getCurrentMonthDateRange(), []);
  const activeDateRange = historyRange === 'month' ? currentMonthDateRange : null;

  // Left panel — last 5 paid records, scoped by the segmented control above.
  const {
    records: historyRecords,
    loading: historyLoading,
    error: historyError,
    refetch: refetchHistory,
  } = useFeeHistory(sessionId, 5, activeDateRange);

  // Right panel — pie chart counts, still via dashboard-summary (unchanged source).
  const {
    counts,
    loading: countsLoading,
    error: countsError,
    refetch: refetchCounts,
  } = useFeeCounts(sessionId, currentMonthYear);

  // Height matching between the two columns needs no JavaScript at all: the
  // outer row below is a flex container with the default `align-items:
  // stretch`, which already makes both columns equal height automatically —
  // the shorter column (right) is stretched to match the taller one's
  // (left's) natural content height. The only requirement is that the right
  // column's content chain uses height: 100% instead of a JS-measured pixel
  // value, so it actually fills the space the browser already gave it,
  // rather than sizing to its own content. (A previous version of this
  // measured the left column's height via ResizeObserver and set it as an
  // explicit pixel style on the right column — that was both unnecessary
  // and the source of a "ResizeObserver loop completed with undelivered
  // notifications" warning, since the JS measurement round-trip fought
  // against layout the browser was already resolving on its own.)

  const handleRefresh = useCallback(async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      await Promise.all([refetchHistory(), refetchCounts()]);
    } finally {
      setIsRefreshing(false);
    }
  }, [isRefreshing, refetchHistory, refetchCounts]);

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'paid':
        return { color: '#16a34a', bgColor: '#ecfdf5', icon: 'pi-check-circle' };
      case 'unpaid':
        return { color: '#dc2626', bgColor: '#fef2f2', icon: 'pi-times-circle' };
      case 'partial':
      case 'partially_paid':
        return { color: '#ea580c', bgColor: '#fff7ed', icon: 'pi-dollar' };
      case 'exempted':
        return { color: '#4f46e5', bgColor: '#eef2ff', icon: 'pi-shield' };
      default:
        return { color: '#6b7280', bgColor: '#f9fafb', icon: 'pi-times-circle' };
    }
  };

  const formatDate = (dateString: string | null) =>
    dateString
      ? new Date(dateString).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
      : '-';

  const studentBodyTemplate = (rowData: HistoryRecord) => {
    const safeId = rowData.student_id;
    return (
      <>
        <Tooltip target={`.student-${safeId}`} content={`Admission No: ${rowData.admission_no || '—'}`} position="top" />
        <div className={`student-${safeId}`}>
          <div style={{ fontWeight: 700, color: '#1e40af', fontSize: 12 }}>{rowData.student_name}</div>
          <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, marginTop: 2 }}>
            Roll: {rowData.roll_no}
            {rowData.receipt_no ? ` · Receipt: ${rowData.receipt_no}` : ''}
          </div>
        </div>
      </>
    );
  };

  const classBodyTemplate = (rowData: HistoryRecord) => (
    <div style={{ fontWeight: 600, color: '#64748b', fontSize: 11 }}>
      {rowData.class_name}
      {rowData.section && ` - ${rowData.section}`}
    </div>
  );

  const statusBodyTemplate = (rowData: HistoryRecord) => {
    const statusLabel = rowData.is_exempted ? 'exempted' : rowData.fees_status;
    const statusConfig = getStatusConfig(statusLabel);
    const safeId = rowData.student_id;
    return (
      <>
        <Tooltip
          target={`.status-${safeId}`}
          content={statusLabel.toUpperCase()}
          position="top"
          style={{ backgroundColor: statusConfig.color, fontSize: '0.7rem', borderRadius: 8, padding: '6px 10px' }}
        />
        <Chip
          icon={`pi ${statusConfig.icon}`}
          label={statusLabel.toUpperCase()}
          className={`status-${safeId}`}
          style={{
            backgroundColor: statusConfig.bgColor,
            color: statusConfig.color,
            fontWeight: 700,
            fontSize: 10,
            height: 22,
            border: `1px solid ${statusConfig.color}15`,
          }}
        />
      </>
    );
  };

  const dateBodyTemplate = (rowData: HistoryRecord) => (
    <div style={{ color: '#94a3b8', fontSize: 11, fontWeight: 600 }}>{formatDate(rowData.paid_date)}</div>
  );

  // The month/year this payment is *for* (record.month_year, e.g. "2026-08"),
  // distinct from paid_date which is *when* the payment was recorded.
  const monthPaidBodyTemplate = (rowData: HistoryRecord) => (
    <div style={{ color: '#1f2937', fontSize: 11, fontWeight: 600 }}>
      {rowData.month_year ? formatMonthYearLabel(rowData.month_year) : '—'}
    </div>
  );

  const payModeBodyTemplate = (rowData: HistoryRecord) => (
    <div style={{ color: '#64748b', fontSize: 11, fontWeight: 600 }}>
      {rowData.pay_mode ? rowData.pay_mode.toUpperCase() : 'N/A'}
    </div>
  );

  const loading = historyLoading || countsLoading;
  const error = historyError || countsError;

  // Loading State
  if (loading && !isRefreshing) {
    return (
      <div
        style={{
          background: 'linear-gradient(180deg, #fafbff 0%, #f1f5f9 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2rem',
        }}
      >
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
        <Card
          style={{
            background: 'rgba(255, 255, 255, 0.95)',
            borderRadius: '16px',
            padding: '2rem',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(59, 130, 246, 0.15)',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
            <div
              style={{
                width: 40,
                height: 40,
                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 20px rgba(59, 130, 246, 0.3)',
              }}
            >
              <i
                className="pi pi-sync"
                style={{
                  color: 'white',
                  fontSize: '1.1rem',
                  animation: 'spin 1s linear infinite',
                }}
              ></i>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontWeight: 700, color: '#1e40af', fontSize: '0.95rem', marginBottom: 4 }}>
                Loading Last Paid Records
              </div>
              <div style={{ color: '#64748b', fontSize: 13, fontWeight: 600 }}>Fetching latest paid fee records...</div>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div
        style={{
          background: 'linear-gradient(180deg, #fafbff 0%, #f1f5f9 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2rem',
        }}
      >
        <Card
          style={{
            background: 'rgba(255, 255, 255, 0.95)',
            borderRadius: '16px',
            padding: '2rem',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(59, 130, 246, 0.15)',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
            <div
              style={{
                width: 40,
                height: 40,
                background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 20px rgba(239, 68, 68, 0.3)',
              }}
            >
              <i className="pi pi-exclamation-circle" style={{ color: 'white', fontSize: '1.1rem' }}></i>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontWeight: 700, color: '#dc2626', fontSize: '0.95rem', marginBottom: 4 }}>
                Unable to Load Fee Data
              </div>
              <div style={{ color: '#64748b', fontSize: 13, fontWeight: 600, marginBottom: 16 }}>{error}</div>
              <Button
                label="Retry Loading"
                onClick={handleRefresh}
                style={{
                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  border: 'none',
                  borderRadius: 999,
                  padding: '6px 16px',
                  fontWeight: 700,
                  fontSize: 12,
                  boxShadow: '0 4px 12px rgba(239, 68, 68, 0.25)',
                }}
              />
            </div>
          </div>
        </Card>
      </div>
    );
  }

  if (counts === null) return null;

  return (
    <div>
      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-3px); }
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .fees-card {
          border-radius: 16px;
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(12px);
          border: 1px solid rgba(59, 130, 246, 0.15);
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
          animation: slideUp 0.6s ease-out, float 6s ease-in-out infinite;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          overflow: visible;
          padding: 20px;
          box-sizing: border-box;
        }

        .fees-card:hover {
          transform: translateY(-6px) scale(1.01);
          box-shadow: 0 12px 32px rgba(59, 130, 246, 0.12);
          background: rgba(255, 255, 255, 0.98);
        }

        .fees-card::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 1px;
          background: linear-gradient(90deg, transparent 0%, rgba(59, 130, 246, 0.3) 50%, transparent 100%);
        }

        .right-card {
          border-radius: 16px;
          background: linear-gradient(135deg, #eff6ff 0%, #ffffff 100%);
          border: 1px solid rgba(59, 130, 246, 0.15);
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
          animation: slideUp 0.8s ease-out, float 6s ease-in-out infinite;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          overflow: hidden;
          padding: 20px;
          box-sizing: border-box;
        }

        .right-card:hover {
          transform: translateY(-6px) scale(1.01);
          box-shadow: 0 12px 32px rgba(59, 130, 246, 0.12);
        }

        .right-card::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 1px;
          background: linear-gradient(90deg, transparent 0%, rgba(59, 130, 246, 0.3) 50%, transparent 100%);
        }

        .icon-container {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 27px;
          height: 27px;
          border-radius: 9px;
          background: linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #60a5fa 100%);
          color: white;
          box-shadow: 0 6px 16px rgba(59, 130, 246, 0.25);
          margin-bottom: 0px;
          flex-shrink: 0;
        }

        .pattern-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          opacity: 0.04;
          pointer-events: none;
          background: url('data:image/svg+xml,<svg width="40" height="40" xmlns="http://www.w3.org/2000/svg"><circle cx="10" cy="10" r="3" fill="none" stroke="rgba(59,130,246,0.1)" stroke-width="1"/><circle cx="30" cy="30" r="2" fill="rgba(59,130,246,0.08)"/></svg>');
        }

        .p-datatable .p-datatable-thead > tr > th {
          background-color: #f8fafc;
          font-weight: 700;
          color: #1e40af;
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          padding: 0.64rem;
        }

        .p-datatable .p-datatable-tbody > tr {
          animation: slideUp 0.5s ease-out;
        }

        .p-datatable .p-datatable-tbody > tr:hover {
          background-color: #f1f5f9;
        }

        .p-datatable .p-datatable-tbody > tr > td {
          padding: 0.64rem;
        }

        .p-datatable .p-datatable-tbody > tr:last-child > td {
          border-bottom: none !important;
        }

        .p-card .p-card-body {
          padding: 20px;
          overflow: visible;
          display: flex !important;
          flex-direction: column !important;
          flex: 1 1 auto !important;
          min-height: 0 !important;
          height: 100%;
        }

        .p-card .p-card-content {
          padding: 0;
          display: flex !important;
          flex-direction: column !important;
          flex: 1 1 auto !important;
          min-height: 0 !important;
          height: 100%;
        }

        .spinning {
          animation: spin 1s linear infinite;
        }
      `}</style>

      <div>
        <div style={{ display: 'flex', gap: '20px' }}>
          {/* Left Container - 70% */}
          <div style={{ flex: '0 0 68%' }}>
            <div className="fees-card" style={{ height: 'auto', display: 'flex', flexDirection: 'column' }}>
              <div className="pattern-overlay" />
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative', zIndex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px', flexWrap: 'wrap', gap: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                    <div className="icon-container">
                      <i className="pi pi-user" style={{ fontSize: '0.8rem' }}></i>
                    </div>
                    <div>
                      <h6 style={{ fontWeight: 700, color: '#1e40af', margin: 0, fontSize: '0.95rem' }}>
                        Last Paid Fee Records
                      </h6>
                      <div style={{ color: '#94a3b8', margin: 0, fontSize: 11, fontWeight: 600, marginTop: 2 }}>
                        {historyRecords.length > 0
                          ? `Last ${historyRecords.length} paid entries${historyRange === 'month' ? ' this month' : ''}`
                          : `No paid records found${historyRange === 'month' ? ' this month' : ''}`}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <SegmentedControl
                      value={historyRange}
                      onChange={setHistoryRange}
                      disabled={isRefreshing}
                    />
                    <Button
                      icon={`pi pi-refresh ${isRefreshing ? 'spinning' : ''}`}
                      rounded
                      outlined
                      onClick={handleRefresh}
                      disabled={isRefreshing}
                      style={{
                        width: 27,
                        height: 27,
                        backgroundColor: 'rgba(59, 130, 246, 0.08)',
                        border: '1px solid rgba(59, 130, 246, 0.2)',
                        color: '#1e40af',
                      }}
                    />
                  </div>
                </div>
                <div style={{ borderTop: '1px solid rgba(59, 130, 246, 0.08)', margin: '14px 0' }} />
                <div
                  style={{
                    flex: 1,
                    overflow: 'auto',
                    minHeight: HISTORY_TABLE_MIN_HEIGHT,
                    opacity: historyLoading ? 0.45 : 1,
                    transition: 'opacity 0.2s ease',
                  }}
                >
                  {historyRecords.length === 0 ? (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 6,
                        fontSize: 11,
                        color: '#cbd5e1',
                        fontStyle: 'italic',
                        padding: '3.4rem 0',
                      }}
                    >
                      <i className="pi pi-info-circle" style={{ fontSize: 11 }}></i>
                      No paid fee records available
                    </div>
                  ) : (
                    <DataTable value={historyRecords} stripedRows>
                      <Column field="student_name" header="Student" body={studentBodyTemplate} style={{ width: '28%' }} />
                      <Column field="class_name" header="Class" body={classBodyTemplate} style={{ width: '10%' }} />
                      <Column field="month_year" header="Month Paid" body={monthPaidBodyTemplate} style={{ width: '16%' }} />
                      <Column field="fees_status" header="Status" body={statusBodyTemplate} style={{ width: '16%' }} />
                      <Column field="paid_date" header="Payment Date" body={dateBodyTemplate} style={{ width: '15%' }} />
                      <Column field="pay_mode" header="Pay Mode" body={payModeBodyTemplate} style={{ width: '15%' }} />
                    </DataTable>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Right Container - 30% */}
          <div style={{ flex: '0 0 30%' }}>
            <div className="right-card" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <div className="pattern-overlay" />
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative', zIndex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px', flexWrap: 'wrap', gap: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                    <div className="icon-container">
                      <i className="pi pi-dollar" style={{ fontSize: '0.8rem' }}></i>
                    </div>
                    <div>
                      <h6 style={{ fontWeight: 700, color: '#1e40af', margin: 0, fontSize: '0.95rem' }}>
                        Payment Overview
                      </h6>
                      <div style={{ color: '#94a3b8', margin: 0, fontSize: 11, fontWeight: 600, marginTop: 2 }}>
                        Summary for {formatMonthYearLabel(currentMonthYear)}
                      </div>
                    </div>
                  </div>
                  <Button
                    icon={`pi pi-refresh ${isRefreshing ? 'spinning' : ''}`}
                    rounded
                    outlined
                    onClick={handleRefresh}
                    disabled={isRefreshing}
                    style={{
                      width: 27,
                      height: 27,
                      backgroundColor: 'rgba(59, 130, 246, 0.08)',
                      border: '1px solid rgba(59, 130, 246, 0.2)',
                      color: '#1e40af',
                    }}
                  />
                </div>
                <div style={{ borderTop: '1px solid rgba(59, 130, 246, 0.08)', margin: '14px 0' }} />
                <div style={{ flex: 1 }}>
                  <EChartsPieChart data={counts} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeesSummary;